import * as React from 'react';
import {
  tokens,
  Button,
  Text,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { ArrowLeftRegular, SlideAddRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import { Header } from '../../components/Header';
import { SyncStatusBar } from '../../components/SyncStatusBar';
import { ErrorBanner } from '../../components/ErrorBanner';
import { presentationMessages, commonMessages } from '../../intl/messages';
import { useStyles } from './PresentationView.styles';
import { usePresentationSlides } from './usePresentationSlides';
import { useInsertSlides } from './useInsertSlides';
import type { PresentationViewProps } from './types';

export function PresentationView({ file, onBack }: PresentationViewProps) {
  const styles = useStyles();
  const intl = useIntl();

  const {
    slides,
    loading,
    error: loadError,
    originalSignedUrl,
  } = usePresentationSlides(file);

  const {
    insert,
    inserting,
    success,
    error: insertError,
    clearStatus,
  } = useInsertSlides();

  const [selectedIndices, setSelectedIndices] = React.useState<Set<number>>(
    new Set()
  );

  const error = loadError ?? insertError;

  function toggleSlide(index: number) {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  function selectAll() {
    setSelectedIndices(new Set(slides.map(s => s.index)));
  }

  function clearSelection() {
    setSelectedIndices(new Set());
  }

  async function handleInsert() {
    if (!originalSignedUrl) {
      return;
    }
    await insert(selectedIndices, slides, originalSignedUrl);
    setSelectedIndices(new Set());
  }

  // Reset insert status when slide selection changes
  React.useEffect(() => {
    clearStatus();
  }, [selectedIndices, clearStatus]);

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.toolbar}>
        <Button
          icon={<ArrowLeftRegular />}
          appearance="subtle"
          size="small"
          onClick={onBack}
          aria-label={intl.formatMessage(commonMessages.back)}
        />
        <Text weight="semibold" truncate style={{ flex: 1 }}>
          {file.title}
        </Text>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className={styles.centered}>
          <Spinner
            size="medium"
            label={intl.formatMessage(presentationMessages.loading)}
          />
        </div>
      ) : (
        <>
          <div className={styles.titleRow}>
            <Text size={200}>
              {intl.formatMessage(presentationMessages.slideCount, {
                count: slides.length,
              })}
            </Text>
            <Button appearance="subtle" size="small" onClick={selectAll}>
              {intl.formatMessage(presentationMessages.selectAll)}
            </Button>
          </div>

          <div className={styles.grid}>
            {slides.map(slide => {
              const selected = selectedIndices.has(slide.index);
              return (
                <div
                  key={slide.index}
                  className={`${styles.slideCard} ${
                    selected ? styles.slideCardSelected : ''
                  }`}
                  onClick={() => toggleSlide(slide.index)}
                  role="checkbox"
                  aria-checked={selected}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSlide(slide.index);
                    }
                  }}
                >
                  {slide.thumbnailUrl ? (
                    <img
                      src={slide.thumbnailUrl}
                      alt={intl.formatMessage(presentationMessages.slideAlt, {
                        index: slide.index,
                      })}
                      className={styles.slideThumbnail}
                    />
                  ) : (
                    <div className={styles.slidePlaceholder}>
                      <Text size={200}>{slide.index}</Text>
                    </div>
                  )}
                  <div className={styles.slideLabel}>
                    <Text size={100}>{slide.index}</Text>
                  </div>
                  {selected && (
                    <div className={styles.checkboxOverlay}>
                      <Badge appearance="filled" color="brand" size="small">
                        ✓
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.footer}>
            {success && (
              <Text style={{ color: tokens.colorStatusSuccessForeground1 }}>
                {intl.formatMessage(presentationMessages.insertSuccess)}
              </Text>
            )}
            <Button
              icon={inserting ? <Spinner size="tiny" /> : <SlideAddRegular />}
              appearance="primary"
              disabled={inserting || slides.length === 0 || !originalSignedUrl}
              onClick={handleInsert}
            >
              {selectedIndices.size > 0
                ? intl.formatMessage(presentationMessages.insertButton, {
                    count: selectedIndices.size,
                  })
                : intl.formatMessage(presentationMessages.insertAll)}
            </Button>
            {selectedIndices.size > 0 && (
              <Button appearance="subtle" size="small" onClick={clearSelection}>
                {intl.formatMessage(presentationMessages.clearSelection)}
              </Button>
            )}
          </div>
        </>
      )}
      <SyncStatusBar />
    </div>
  );
}
